// src/pages/CRM/Finance/PaymentDialog.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Autocomplete, MenuItem,
  InputAdornment, CircularProgress, Box, Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { PaymentMethod, PaymentMethodLabels } from '../Core/crmEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const PAYMENT_METHODS = Object.entries(PaymentMethod).map(([key, value]) => ({
  value,
  label: PaymentMethodLabels[key] || key,
}));

const PaymentDialog = ({ open, onClose, onSaved, contactId, invoiceId }) => {
  const { chatbotId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [saving, setSaving] = useState(false);

  // Contact search
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Invoice search (filtered by contact)
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Form fields
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.BANK_TRANSFER);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setAmount('');
      setPaymentDate(new Date());
      setPaymentMethod(PaymentMethod.BANK_TRANSFER);
      setReferenceNumber('');
      setNotes('');
      setSelectedInvoice(null);

      if (contactId) {
        // Pre-set contact
        loadContact(contactId);
      } else {
        setSelectedContact(null);
        // Load top 5 contacts by default
        searchContacts('');
      }

      if (invoiceId) {
        loadInvoice(invoiceId);
      }
    }
  }, [open, contactId, invoiceId]);

  // Load invoices when contact changes
  useEffect(() => {
    if (selectedContact && !invoiceId) {
      loadContactInvoices(selectedContact.id);
    }
  }, [selectedContact]);

  const loadContact = async (id) => {
    try {
      const response = await axiosInstance.get(`/api/crm/contacts/${id}`);
      if (response.data.success) {
        const c = response.data.data;
        setSelectedContact(c);
        setContactOptions([c]);
      }
    } catch (err) {
      console.error('Error loading contact:', err);
    }
  };

  const loadInvoice = async (id) => {
    try {
      const response = await axiosInstance.get(`/api/crm/invoices/${id}`);
      if (response.data.success) {
        const inv = response.data.data;
        setSelectedInvoice(inv);
        setInvoiceOptions([inv]);
        // Pre-fill amount with balance due
        if (inv.balance_due) {
          setAmount(String(inv.balance_due));
        }
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
    }
  };

  const loadContactInvoices = async (cId) => {
    setInvoiceLoading(true);
    try {
      const params = {
        contact_id: cId,
        status: 'SENT,PARTIALLY_PAID,OVERDUE',
        per_page: 50,
        ...(chatbotId && { chatbot_id: chatbotId }),
      };
      const response = await axiosInstance.get('/api/crm/invoices', { params });
      if (response.data.success) {
        setInvoiceOptions(response.data.data?.invoices || []);
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const searchContacts = useCallback(async (query) => {
    setContactLoading(true);
    try {
      const params = { per_page: query ? 10 : 5 };
      if (query) params.search = query;
      if (chatbotId) params.chatbot_id = chatbotId;
      const response = await axiosInstance.get('/api/crm/contacts', { params });
      if (response.data.success) {
        setContactOptions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error searching contacts:', err);
    } finally {
      setContactLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    if (contactId) return; // Skip search if contact is pre-set
    const timer = setTimeout(() => {
      searchContacts(contactSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch, searchContacts, contactId]);

  const handleSave = async () => {
    if (!selectedContact && !contactId) {
      enqueueSnackbar('Please select a contact', { variant: 'warning' });
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      enqueueSnackbar('Please enter a valid amount', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contact_id: selectedContact?.id || contactId,
        invoice_id: selectedInvoice?.id || invoiceId || undefined,
        amount: parseFloat(amount),
        payment_date: paymentDate ? paymentDate.toISOString().split('T')[0] : undefined,
        payment_method: paymentMethod,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
        ...(chatbotId && { chatbot_id: chatbotId }),
      };

      const response = await axiosInstance.post('/api/crm/payments', payload);
      if (response.data.success) {
        enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
        if (onSaved) onSaved();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to record payment', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      enqueueSnackbar('Failed to record payment', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amt) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>Record Payment</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Contact selector */}
            {!contactId && (
              <Grid item xs={12}>
                <Autocomplete
                  options={contactOptions}
                  getOptionLabel={(option) =>
                    `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || ''
                  }
                  filterOptions={(x) => x}
                  value={selectedContact}
                  onChange={(e, val) => {
                    setSelectedContact(val);
                    setSelectedInvoice(null);
                  }}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input') setContactSearch(val);
                  }}
                  onOpen={() => { if (!contactOptions.length) searchContacts(''); }}
                  loading={contactLoading}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography sx={{ fontFamily, fontSize: 14 }}>
                          {`${option.first_name || ''} ${option.last_name || ''}`.trim()}
                        </Typography>
                        {option.email && (
                          <Typography sx={{ fontFamily, fontSize: 12, color: '#6b7280' }}>
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact *"
                      placeholder="Search contacts..."
                      size="small"
                      sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Invoice selector */}
            {!invoiceId && (
              <Grid item xs={12}>
                <Autocomplete
                  options={invoiceOptions}
                  getOptionLabel={(option) =>
                    `${option.invoice_number || 'Invoice'} - ${formatCurrency(option.balance_due)} due`
                  }
                  value={selectedInvoice}
                  onChange={(e, val) => {
                    setSelectedInvoice(val);
                    if (val?.balance_due) {
                      setAmount(String(val.balance_due));
                    }
                  }}
                  loading={invoiceLoading}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  disabled={!selectedContact && !contactId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Invoice (optional)"
                      placeholder="Select invoice..."
                      size="small"
                      sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Amount *"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              />
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Payment Date"
                value={paymentDate}
                onChange={(val) => setPaymentDate(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }} />
                )}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Method"
                select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                size="small"
                fullWidth
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              >
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value} sx={{ fontFamily }}>
                    {method.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reference Number"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                size="small"
                fullWidth
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
